import type { NavigationSnapshot } from "../core/Game";

const MAP_RADIUS_METERS = 180;
const DIRECTIONS = [
  "north",
  "northeast",
  "east",
  "southeast",
  "south",
  "southwest",
  "west",
  "northwest",
] as const;

export class Minimap {
  readonly canvas: HTMLCanvasElement;

  constructor() {
    this.canvas = document.createElement("canvas");
    this.canvas.className = "minimap";
    this.canvas.dataset.hud = "minimap";
    this.canvas.setAttribute("role", "img");
  }

  render(snapshot: NavigationSnapshot): void {
    const cssSize = this.canvas.clientWidth || 184;
    const pixelRatio = Math.min(2, window.devicePixelRatio || 1);
    const backingSize = Math.max(1, Math.round(cssSize * pixelRatio));
    if (this.canvas.width !== backingSize || this.canvas.height !== backingSize) {
      this.canvas.width = backingSize;
      this.canvas.height = backingSize;
    }
    const context = this.canvas.getContext("2d");
    if (!context) return;

    const styles = getComputedStyle(document.documentElement);
    const muted = styles.getPropertyValue("--muted").trim() || "#91a6a8";
    const accent = styles.getPropertyValue("--accent").trim() || "#f2ca54";
    const primary = styles.getPropertyValue("--primary").trim() || "#2e8792";
    const error = styles.getPropertyValue("--error").trim() || "#d35b50";
    const ink = styles.getPropertyValue("--ink").trim() || "#eef4f4";
    const center = backingSize / 2;
    const mapRadius = center - 1 * pixelRatio;
    const worldScale = mapRadius / MAP_RADIUS_METERS;
    const headingRadians = (snapshot.headingDegrees * Math.PI) / 180;

    context.resetTransform();
    context.clearRect(0, 0, backingSize, backingSize);
    context.save();
    context.beginPath();
    context.arc(center, center, mapRadius, 0, Math.PI * 2);
    context.clip();
    context.fillStyle = "oklch(0.1 0 0 / 92%)";
    context.fillRect(0, 0, backingSize, backingSize);
    context.translate(center, center);
    context.rotate(-headingRadians);
    context.scale(worldScale, worldScale);
    context.translate(-snapshot.player.x, -snapshot.player.z);

    context.strokeStyle = muted;
    context.globalAlpha = 0.55;
    context.lineCap = "round";
    for (const [fromX, fromZ, toX, toZ, width] of snapshot.roads) {
      context.lineWidth = Math.min(4, Math.max(1, width * worldScale * 0.16)) / worldScale;
      context.beginPath();
      context.moveTo(fromX, fromZ);
      context.lineTo(toX, toZ);
      context.stroke();
    }
    context.globalAlpha = 1;

    for (const car of snapshot.cars) {
      context.save();
      context.translate(car.x, car.z);
      const driven = car.state === "driving";
      context.fillStyle = driven ? primary : accent;
      context.strokeStyle = driven ? ink : accent;
      context.lineWidth = (driven ? 2 : 1) / worldScale;
      context.beginPath();
      context.rect(-3.2, -1.7, 6.4, 3.4);
      context.fill();
      context.stroke();
      if (driven) {
        context.beginPath();
        context.moveTo(1.1, -1.7);
        context.lineTo(3.2, 0);
        context.lineTo(1.1, 1.7);
        context.stroke();
      }
      if (car.state === "disabled") {
        context.strokeStyle = error;
        context.lineWidth = 2.2 / worldScale;
        context.beginPath();
        context.moveTo(-2.4, -2.4);
        context.lineTo(2.4, 2.4);
        context.moveTo(2.4, -2.4);
        context.lineTo(-2.4, 2.4);
        context.stroke();
      }
      context.restore();
    }

    if (snapshot.bus) {
      context.fillStyle = accent;
      context.fillRect(snapshot.bus.x - 5, snapshot.bus.z - 1.8, 10, 3.6);
    }
    if (snapshot.train) {
      context.fillStyle = accent;
      context.fillRect(snapshot.train.x - 7, snapshot.train.z - 1.7, 6.2, 3.4);
      context.fillRect(snapshot.train.x + 0.8, snapshot.train.z - 1.7, 6.2, 3.4);
      context.strokeStyle = accent;
      context.lineWidth = 1.2 / worldScale;
      context.beginPath();
      context.moveTo(snapshot.train.x - 0.8, snapshot.train.z);
      context.lineTo(snapshot.train.x + 0.8, snapshot.train.z);
      context.stroke();
    }
    context.restore();

    context.save();
    context.translate(center, center);
    context.fillStyle = ink;
    context.beginPath();
    context.moveTo(0, -9 * pixelRatio);
    context.lineTo(6 * pixelRatio, 7 * pixelRatio);
    context.lineTo(0, 4 * pixelRatio);
    context.lineTo(-6 * pixelRatio, 7 * pixelRatio);
    context.closePath();
    context.fill();
    const northRadius = mapRadius - 13 * pixelRatio;
    const northX = -Math.sin(headingRadians) * northRadius;
    const northY = -Math.cos(headingRadians) * northRadius;
    context.font = `700 ${11 * pixelRatio}px system-ui`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText("N", northX, northY);
    context.restore();

    const directionIndex = Math.round(snapshot.headingDegrees / 45) % 8;
    this.canvas.setAttribute(
      "aria-label",
      `Heading-up minimap. Facing ${DIRECTIONS[directionIndex]}. Nearby streets and vehicles.`,
    );
  }

  dispose(): void {
    this.canvas.width = 0;
    this.canvas.height = 0;
    this.canvas.remove();
  }
}
